import React from 'react';
import {Link} from 'react-router-dom';
import {TrendingTopic} from '../../../../services/socialFeedApi';
import {TalentRecommendation} from '../../../../services/talentSearchApi';
import {getAssetUrl} from '../../../../utils/assets';
import styles from '../Home.module.css';

interface HomeSidebarQuickStat {
  label: string;
  value: string;
  highlighted?: boolean;
}

interface HomeSidebarProps {
  quickStats: HomeSidebarQuickStat[];
  trendingTopics: TrendingTopic[];
  trendingTopicsLoading: boolean;
  trendingTopicsError: string | null;
  activeTopicKey: string | null;
  onSelectTopic: (topic: TrendingTopic) => void;
  onRetryTrendingTopics: () => void;
  suggestions: TalentRecommendation[];
  suggestionsLoading: boolean;
  suggestionsError: string | null;
  suggestionFollowError: string | null;
  pendingSuggestionFollowIds: Record<string, boolean>;
  onRetrySuggestions: () => void;
  onDismissSuggestionFollowError: () => void;
  onFollowSuggestion: (userId: string) => void;
  onViewSuggestion: (userId: string) => void;
}

const iconUrl = (fileName: string) => `${process.env.PUBLIC_URL}/icons/${fileName}`;

const quickStatsIconMap: Record<string, string> = {
  Followers: iconUrl('user-group.svg'),
  Following: iconUrl('user-fill.svg'),
  Posts: iconUrl('post-fill.svg'),
  Messages: iconUrl('message-fill.svg'),
};

const iconMaskStyle = (propertyName: string, url: string) => ({
  [propertyName]: `url("${url}")`,
}) as React.CSSProperties;

const getInitials = (name: string) => name
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((part) => part[0]?.toUpperCase() || '')
  .join('') || 'XM';

const SuggestionRow: React.FC<{
  suggestion: TalentRecommendation;
  pending: boolean;
  onFollow: () => void;
  onView: () => void;
}> = ({suggestion, pending, onFollow, onView}) => {
  const name = suggestion.profile?.name?.trim() || 'XPSR Member';
  const avatarUrl = getAssetUrl(suggestion.profile?.profilePicture);
  const [avatarFailed, setAvatarFailed] = React.useState(false);

  React.useEffect(() => setAvatarFailed(false), [avatarUrl]);

  return (
    <div className={styles.suggestionRow}>
      <button
        type="button"
        className={styles.suggestionAvatarButton}
        aria-label={`View ${name}'s profile`}
        onClick={onView}
      >
        <span className={styles.suggestionAvatar}>
          {avatarUrl && !avatarFailed ? (
            <img src={avatarUrl} alt="" loading="lazy" onError={() => setAvatarFailed(true)} />
          ) : (
            <span aria-hidden="true">{getInitials(name)}</span>
          )}
        </span>
      </button>
      <div className={styles.suggestionContent}>
        <button type="button" className={styles.suggestionName} onClick={onView}>{name}</button>
        <p className={styles.suggestionMeta}>Suggested for you</p>
      </div>
      <button
        type="button"
        className={styles.followButton}
        disabled={pending}
        aria-label={`${pending ? 'Following' : 'Follow'} ${name}`}
        aria-busy={pending}
        onClick={onFollow}
      >
        {pending ? 'Following…' : 'Follow'}
      </button>
    </div>
  );
};

const HomeSidebar: React.FC<HomeSidebarProps> = ({
  quickStats,
  trendingTopics,
  trendingTopicsLoading,
  trendingTopicsError,
  activeTopicKey,
  onSelectTopic,
  onRetryTrendingTopics,
  suggestions,
  suggestionsLoading,
  suggestionsError,
  suggestionFollowError,
  pendingSuggestionFollowIds,
  onRetrySuggestions,
  onDismissSuggestionFollowError,
  onFollowSuggestion,
  onViewSuggestion,
}) => (
  <div className={styles.sidebarColumn}>
    <div className={`card ${styles.sidebarCard} ${styles.sidebarCardSpaced}`}>
      <h3 className={styles.sidebarTitle}>
        <span
          className={`${styles.sidebarTitleIcon} ${styles.quickStatsTitleIcon}`}
          style={iconMaskStyle('--sidebar-title-icon-url', iconUrl('quick-stats.svg'))}
          aria-hidden="true"
        />
        <span>Quick Stats</span>
      </h3>
      <div className={styles.quickStatsList}>
        {quickStats.map((stat) => (
          <p key={stat.label} className={styles.quickStatRow}>
            <span className={styles.quickStatLabel}>
              <span
                className={styles.quickStatIcon}
                style={iconMaskStyle('--quick-stat-icon-url', quickStatsIconMap[stat.label] || iconUrl('analytics-fill.svg'))}
                aria-hidden="true"
              />
              <span>{stat.label}</span>
            </span>
            <strong className={stat.highlighted ? styles.quickStatValueHighlighted : ''}>{stat.value}</strong>
          </p>
        ))}
      </div>
    </div>

    <div id="trending-topics" className={`card ${styles.sidebarCard} ${styles.sidebarCardSpaced}`}>
      <h3 className={styles.sidebarTitle}>
        <span
          className={styles.sidebarTitleIcon}
          style={iconMaskStyle('--sidebar-title-icon-url', iconUrl('trending-up-rounded.svg'))}
          aria-hidden="true"
        />
        <span>Trending Now</span>
      </h3>
      <div className={styles.trendingList}>
        {trendingTopicsLoading && trendingTopics.length === 0 ? (
          Array.from({length: 4}, (_, index) => (
            <span key={index} className={styles.trendingTagSkeleton} aria-hidden="true" />
          ))
        ) : trendingTopicsError && trendingTopics.length === 0 ? (
          <div className={styles.trendingStatus} role="alert">
            <span>Trending topics are temporarily unavailable.</span>
            <button type="button" className={styles.trendingRetryButton} onClick={onRetryTrendingTopics}>
              Retry
            </button>
          </div>
        ) : trendingTopics.length === 0 ? (
          <p className={styles.trendingStatus}>No topics meet the activity threshold yet.</p>
        ) : (
          <>
          {trendingTopics.map((topic) => {
            const activityLabel = `${topic.contentCount} active content ${topic.contentCount === 1 ? 'item' : 'items'}`;

            return (
              <button
                key={topic.key}
                type="button"
                className={`${styles.trendingTag} ${activeTopicKey === topic.key ? styles.trendingTagActive : ''}`.trim()}
                aria-label={`${topic.label}, ${activityLabel}`}
                aria-pressed={activeTopicKey === topic.key}
                title={activityLabel}
                onClick={() => onSelectTopic(topic)}
              >
                {topic.label}
              </button>
            );
          })}
          {trendingTopicsError ? (
            <div className={styles.trendingStatus} role="status">
              <span>Showing recently loaded topics.</span>
              <button type="button" className={styles.trendingRetryButton} onClick={onRetryTrendingTopics}>Retry</button>
            </div>
          ) : null}
          </>
        )}
      </div>
    </div>

    <div className={`card ${styles.sidebarCard}`}>
      <h3 className={styles.sidebarTitle}>
        <span
          className={styles.sidebarTitleIcon}
          style={iconMaskStyle('--sidebar-title-icon-url', iconUrl('profile-fill.svg'))}
          aria-hidden="true"
        />
        <span>Suggestions</span>
      </h3>
      <div className={styles.suggestionsList} aria-busy={suggestionsLoading}>
        {suggestionsLoading && suggestions.length === 0 ? (
          Array.from({length: 3}, (_, index) => (
            <div key={index} className={styles.suggestionRowSkeleton} aria-hidden="true">
              <span className={styles.suggestionAvatarSkeleton} />
              <span className={styles.suggestionTextSkeleton}>
                <i />
                <i />
              </span>
              <span className={styles.suggestionButtonSkeleton} />
            </div>
          ))
        ) : suggestionsError && suggestions.length === 0 ? (
          <div className={styles.suggestionsStatus} role="alert">
            <span>Suggestions are temporarily unavailable.</span>
            <button type="button" onClick={onRetrySuggestions}>Retry</button>
          </div>
        ) : suggestions.length === 0 ? (
          <p className={styles.suggestionsStatus}>You’re all caught up.</p>
        ) : (
          suggestions.map((suggestion) => (
            <SuggestionRow
              key={suggestion._id}
              suggestion={suggestion}
              pending={Boolean(pendingSuggestionFollowIds[suggestion._id])}
              onFollow={() => onFollowSuggestion(suggestion._id)}
              onView={() => onViewSuggestion(suggestion._id)}
            />
          ))
        )}
      </div>
      {suggestionsError && suggestions.length > 0 ? (
        <div className={styles.suggestionsInlineError} role="status">
          <span>Couldn’t refresh suggestions.</span>
          <button type="button" onClick={onRetrySuggestions}>Retry</button>
        </div>
      ) : null}
      {suggestionFollowError ? (
        <div className={styles.suggestionsInlineError} role="alert">
          <span>{suggestionFollowError}</span>
          <button type="button" onClick={onDismissSuggestionFollowError}>Dismiss</button>
        </div>
      ) : null}
      <Link to="/discover-talent" className={styles.viewAllSuggestionsButton}>
        <span>View all suggestions</span>
        <span
          className={styles.viewAllSuggestionsIcon}
          style={iconMaskStyle('--view-all-suggestions-icon-url', iconUrl('arrow-right-bold.svg'))}
          aria-hidden="true"
        />
      </Link>
    </div>
  </div>
);

export default React.memo(HomeSidebar);
